import Vue from 'vue';

export const ApckTrustReporting = {
    data() {
        return {
            loading: false,
            reports: [
                {
                    id: 'active-monitor-summary',
                    title: 'Active Monitor Summary',
                    description: 'Every page being monitored for script, header and iframe tampering, with the status of its most recent check. Includes pages with monitoring turned off and pages that have not run yet.',
                    icon: 'fa-shield-halved',
                    format: 'HTML',
                    category: 'Compliance',
                    features: [
                        'Status of every monitored page',
                        'Pages checked behind a sign-in',
                        'Script and iframe counts',
                        'Self-contained - opens with no network access'
                    ],
                    url: '/_active-monitor/report/summary',
                    // Only shown where Active Monitor is turned on for the organisation.
                    // Without this the card would offer a report that 403s.
                    requires: 'enable_active_checks',
                    params: {}
                },
                {
                    id: 'executive-summary',
                    title: 'Executive Summary Report',
                    description: 'Comprehensive high-level overview of third-party trust relationships, risk distribution, and vulnerabilities designed for executive management.',
                    icon: 'fa-chart-pie',
                    format: 'PDF',
                    category: 'Executive',
                    features: [
                        'Overall risk score and assessment',
                        'Risk and vulnerability distribution',
                        'Top third-party services',
                        'Executive recommendations'
                    ],
                    url: '/trust/reporting/executive-summary',
                    params: {
                        format: 'pdf'
                    }
                },
                {
                    id: 'thirdparty-suppliers',
                    title: 'Third-Party Suppliers Report',
                    description: 'Detailed Excel spreadsheet containing comprehensive information about all third-party suppliers, their risk levels, vulnerabilities, and geographic locations.',
                    icon: 'fa-file-excel',
                    format: 'Excel',
                    category: 'Detailed',
                    features: [
                        'Complete supplier inventory',
                        'Risk level classifications',
                        'Vulnerability counts by severity',
                        'Geographic distribution',
                        'Domain relationships',
                        'CNAME details'
                    ],
                    url: '/trust/reporting/thirdparties',
                    params: {}
                },
                {
                    id: 'vulnerabilities-html',
                    title: 'Vulnerabilities Report (HTML)',
                    description: 'Comprehensive HTML report of all trust monitor vulnerabilities with detailed technical information, trust relationships, and remediation guidance.',
                    icon: 'fa-file-code',
                    format: 'HTML',
                    category: 'Technical',
                    features: [
                        'Complete vulnerability details',
                        'Trust relationship context',
                        'Impact and status tracking',
                        'Technical narratives',
                        'Remediation solutions',
                        'Interactive navigation'
                    ],
                    url: '/trust/reporting/vulnerabilities',
                    params: {}
                }/*,
                {
                    id: 'vulnerabilities-pdf',
                    title: 'Vulnerabilities Report (PDF)',
                    description: 'Printable PDF report of all trust monitor vulnerabilities, ideal for sharing with technical teams and stakeholders requiring detailed security findings.',
                    icon: 'fa-file-pdf',
                    format: 'PDF',
                    category: 'Technical',
                    features: [
                        'Print-ready format',
                        'Complete vulnerability details',
                        'Trust relationship context',
                        'Impact and status tracking',
                        'Technical narratives',
                        'Remediation solutions'
                    ],
                    url: '/trust/reporting/vulnerabilities/pdf',
                    params: {}
                }*/
            ]
        };
    },
    computed: {
        /**
         * Reports this organisation can actually run.
         *
         * Every card used to render unconditionally, which was fine while every report
         * applied to everyone. A card for a service the organisation does not have is an
         * advert for a 403.
         */
        visibleReports: function() {
            var permissions = this.$permissions || {};
            return this.reports.filter(function(report) {
                return !report.requires || !!permissions[report.requires];
            });
        },
    },
    methods: {
        downloadReport(report) {
            let cmp = this;

            console.log('Downloading report:', report.title);

            // Show loading indicator
            cmp.loading = true;

            // Build query parameters
            let queryString = Object.keys(report.params)
                .map(key =>
                    encodeURIComponent(key) + '=' + encodeURIComponent(report.params[key])
                )
                .join('&');

            let url = report.url + (queryString ? '?' + queryString : '');

            // Use fetch API for better download handling
            fetch(url, {
                credentials: 'same-origin'
            })
            .then(response => {
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }

                // Get the filename from the Content-Disposition header if available
                let filename = `trust_monitor_${report.id}.${report.format.toLowerCase()}`;

                const contentDisposition = response.headers.get('Content-Disposition');
                if (contentDisposition) {
                    const filenameMatch = contentDisposition.match(
                        /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
                    );
                    if (filenameMatch && filenameMatch[1]) {
                        filename = filenameMatch[1].replace(/['"]/g, '');
                    }
                }

                return response.blob().then(blob => ({ blob, filename }));
            })
            .then(({ blob, filename }) => {
                // Create a URL for the blob and trigger download
                const downloadUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');

                link.href = downloadUrl;
                link.download = filename;

                document.body.appendChild(link);
                link.click();

                // Clean up
                window.URL.revokeObjectURL(downloadUrl);
                document.body.removeChild(link);

                cmp.loading = false;
            })
            .catch(error => {
                console.error('Error downloading report:', error);
                cmp.loading = false;

                // Fallback to direct URL navigation
                console.log('Falling back to direct URL navigation');
                window.location.href = url;
            });
        },
        getFormatIcon(format) {
            const formatIcons = {
                'PDF': 'fa-file-pdf',
                'Excel': 'fa-file-excel',
                'JSON': 'fa-file-code',
                'HTML': 'fa-file-code'
            };
            return formatIcons[format] || 'fa-file';
        },
        getCategoryColor(category) {
            const categoryColors = {
                'Executive': 'blue',
                'Detailed': 'green',
                'Technical': 'purple',
                'Compliance': 'orange'
            };
            return categoryColors[category] || 'light-slate';
        }
    },
    template: `
        <ui-page title="Trust Monitor Reports" stickyTitle>
            <template #content>
                <ui-layout type="grid" cols="3">
                    <ui-panel-base
                        v-for="report in visibleReports"
                        :key="report.id"
                        direction="column"
                        gap="6"
                    >
                        <ui-layout direction="column" gap="6">
                            <ui-layout align="center" gap="3">
                                <ui-panel-base color="light-slate">
                                    <ui-icon :icon="report.icon" size="lg" />
                                </ui-panel-base>

                                <ui-layout direction="column" gap="1">
                                    <ui-heading tag="h3" size="md">{{ report.title }}</ui-heading>
                                    <span class="text--sm text--secondary">{{ report.format }}</span>
                                </ui-layout>
                            </ui-layout>

                            <p>{{ report.description }}</p>

                            <ui-layout direction="column">
                                <ui-heading size="md" color="secondary">
                                    Key Features:
                                </ui-heading>
                                <ui-layout tag="ul" gap="2" direction="column">
                                    <ui-layout
                                        v-for="feature in report.features"
                                        :key="feature"
                                        tag="li"
                                        gap="2"
                                        align="center"
                                    >
                                        <ui-icon icon="fa-check-circle" size="sm" type="success" />
                                        <span class="text--sm text--secondary">{{ feature }}</span>
                                    </ui-layout>
                                </ui-layout>
                            </ui-layout>
                        </ui-layout>

                        <ui-button
                            :label="'Download ' + report.format"
                            iconLeft="fa-download"
                            type="primary"
                            size="md"
                            @click="downloadReport(report)"
                        />
                    </ui-panel-base>
                </ui-layout>
                
                <ui-spinner :show="loading" theme="dark" />
            </template>
        </ui-page>
    `
};
